import { useState, useEffect } from 'react';
import { api } from '../api/client';

const W = 480, H = 320;
const PAD = { l: 44, r: 16, t: 24, b: 40 };
const PW = W - PAD.l - PAD.r;
const PH = H - PAD.t - PAD.b;

const PROJECT_DAYS = 30;

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function linearSlope(values) {
  const n = values.length;
  if (n < 2) return values[0] || 0;
  const sumX  = values.reduce((s, _, i) => s + i, 0);
  const sumY  = values.reduce((s, v) => s + v, 0);
  const sumXY = values.reduce((s, v, i) => s + i * v, 0);
  const sumX2 = values.reduce((s, _, i) => s + i * i, 0);
  return (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
}

function fmtOrders(v) {
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : Math.round(v).toString();
}

export default function SalesProjectionChart({ mode }) {
  const [overTime, setOverTime] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tooltip, setTooltip]   = useState(null);
  const [lookback, setLookback] = useState(14);

  const isBatch = mode === 'current_batch';
  const qs = isBatch ? '?mode=current_batch' : '';

  useEffect(() => {
    api.get(`/admin/sales/over-time${qs}`)
      .then(setOverTime)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [qs]);

  if (loading) {
    return (
      <div className="card">
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>Loading…</p>
      </div>
    );
  }

  const title = isBatch ? 'Current Batch Order Projection' : 'Sales Order Projection';

  if (!overTime.length) {
    return (
      <div className="card">
        <h3 style={{ marginBottom: 8 }}>{title}</h3>
        <div className="empty-state" style={{ padding: 40 }}>No orders yet</div>
      </div>
    );
  }

  // Build cumulative order series
  let cumOrders = 0;
  const actual = overTime.map(d => {
    const orders = parseInt(d.order_count, 10) || 0;
    cumOrders += orders;
    return {
      date:   d.date,
      cum:    cumOrders,
      orders,
    };
  });

  const lastDate  = actual[actual.length - 1].date;
  const totalNow  = actual[actual.length - 1].cum;

  // Slope from lookback window
  const slice      = overTime.slice(-lookback);
  const dailyCounts = slice.map(d => parseInt(d.order_count, 10) || 0);
  const slope       = linearSlope(dailyCounts);
  const bestSlope   = slope * 1.5;
  const worstSlope  = Math.max(0, slope * 0.5);

  // Confidence band (±1σ)
  const mean   = dailyCounts.reduce((s, v) => s + v, 0) / dailyCounts.length;
  const stdDev = Math.sqrt(dailyCounts.reduce((s, v) => s + (v - mean) ** 2, 0) / dailyCounts.length);

  // Projection points
  const projected = [];
  for (let i = 1; i <= PROJECT_DAYS; i++) {
    projected.push({
      date:  addDays(lastDate, i),
      cum:   totalNow + slope * i,
      best:  totalNow + bestSlope * i,
      worst: totalNow + worstSlope * i,
    });
  }

  const projEnd      = totalNow + slope * PROJECT_DAYS;
  const projEndBest  = totalNow + bestSlope * PROJECT_DAYS;
  const avgDaily     = mean;
  const busiest      = actual.reduce((a, b) => b.orders > a.orders ? b : a, actual[0]);
  const quietest     = actual.reduce((a, b) => b.orders < a.orders ? b : a, actual[0]);

  // Scaling
  const allCums = [
    ...actual.map(p => p.cum),
    ...projected.map(p => p.cum),
    ...projected.map(p => p.best),
    ...projected.map(p => p.worst),
  ];
  const minY   = 0;
  const maxY   = Math.max(...allCums, 1);
  const rangeY = maxY - minY || 1;

  const totalPts = actual.length + projected.length;
  const toX = i => PAD.l + (i / (totalPts - 1)) * PW;
  const toY = v => PAD.t + PH - ((v - minY) / rangeY) * PH;

  const gridSteps = 4;
  const grids = Array.from({ length: gridSteps + 1 }, (_, i) => {
    const v = minY + (rangeY / gridSteps) * i;
    return { v, y: toY(v) };
  });

  const projOffset = actual.length - 1;
  const actualPts  = actual.map((p, i) => `${toX(i).toFixed(1)},${toY(p.cum).toFixed(1)}`).join(' ');

  const projLine = (getCum) =>
    [actual[actual.length - 1], ...projected]
      .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY(getCum(p, i)).toFixed(1)}`).join(' ');

  const centreProj = projLine((p) => p.cum ?? totalNow);
  const bestProj   = projLine((p, i) => i === 0 ? totalNow : p.best);
  const worstProj  = projLine((p, i) => i === 0 ? totalNow : p.worst);

  const upperBand = [actual[actual.length - 1], ...projected]
    .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY((p.cum ?? totalNow) + stdDev * Math.sqrt(i)).toFixed(1)}`).join(' ');
  const lowerBandRev = [actual[actual.length - 1], ...projected]
    .map((p, i) => `${toX(projOffset + i).toFixed(1)},${toY(Math.max(0, (p.cum ?? totalNow) - stdDev * Math.sqrt(i))).toFixed(1)}`).reverse().join(' ');
  const bandPolygon = upperBand + ' ' + lowerBandRev;

  const dividerX   = toX(actual.length - 1);
  const xLabelStep = Math.ceil(totalPts / 6);
  const busiestIdx = actual.findIndex(p => p.date === busiest.date);

  const trendArrow = slope > 0.1 ? '↑' : slope < -0.1 ? '↓' : '→';
  const trendColor = slope > 0.1 ? 'var(--success)' : slope < -0.1 ? 'var(--danger)' : 'var(--text-muted)';

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setLookback(d)}
              style={{
                padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 20, cursor: 'pointer',
                border: lookback === d ? 'none' : '1px solid var(--border)',
                background: lookback === d ? 'var(--accent)' : 'var(--bg-secondary)',
                color: lookback === d ? '#fff' : 'var(--text-muted)',
              }}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
        {isBatch ? 'Current batch only · ' : ''}Cumulative order count &middot; dashed = {PROJECT_DAYS}-day projection
      </p>

      {/* Summary chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--success)' }}>{fmtOrders(totalNow)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Total Orders</div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: trendColor }}>
            {trendArrow} {Math.abs(slope).toFixed(1)}/day
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Trend ({lookback}d avg)</div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#63b3ed' }}>{fmtOrders(projEnd)}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            In {PROJECT_DAYS} Days
            <span style={{ marginLeft: 4, color: 'var(--success)' }}>(+{fmtOrders(projEnd - totalNow)})</span>
          </div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-secondary)' }}>
            {avgDaily.toFixed(1)}/day
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Avg Orders/Day</div>
        </div>

        <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#fbd38d' }}>{busiest.orders}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Busiest Day
            <span style={{ display: 'block' }}>{busiest.date.slice(5)}</span>
          </div>
        </div>

        {quietest.orders !== busiest.orders && (
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', padding: '8px 14px', flex: '1 0 110px' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-muted)' }}>{quietest.orders}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              Quietest Day
              <span style={{ display: 'block' }}>{quietest.date.slice(5)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Chart */}
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', minWidth: 320, height: 'auto' }}>
          {grids.map(({ v, y }) => (
            <g key={v}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y}
                stroke="rgba(255,255,255,0.12)" strokeWidth={1} />
              <text x={PAD.l - 5} y={y + 4} textAnchor="end" fontSize={11} fill="var(--text-muted)">
                {fmtOrders(v)}
              </text>
            </g>
          ))}

          <polygon points={bandPolygon} fill="rgba(99,179,237,0.08)" />

          <polyline points={worstProj} fill="none"
            stroke="var(--danger)" strokeWidth={1} strokeDasharray="3 4" opacity={0.35}
            strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={bestProj} fill="none"
            stroke="var(--success)" strokeWidth={1} strokeDasharray="3 4" opacity={0.35}
            strokeLinejoin="round" strokeLinecap="round" />

          <line x1={dividerX} x2={dividerX} y1={PAD.t} y2={PAD.t + PH}
            stroke="var(--text-muted)" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.6} />
          <text x={dividerX + 5} y={PAD.t + 13} fontSize={11} fill="var(--text-muted)">Today</text>

          <polyline points={centreProj} fill="none"
            stroke="#63b3ed" strokeWidth={2.5} strokeDasharray="7 4"
            strokeLinejoin="round" strokeLinecap="round" />

          <polyline points={actualPts} fill="none"
            stroke="#48bb78" strokeWidth={3}
            strokeLinejoin="round" strokeLinecap="round" />

          {busiestIdx >= 0 && (
            <g>
              <circle cx={toX(busiestIdx)} cy={toY(busiest.cum)} r={5}
                fill="#fbd38d" stroke="var(--bg-card)" strokeWidth={2} />
              <text x={toX(busiestIdx)} y={toY(busiest.cum) - 9} textAnchor="middle"
                fontSize={10} fill="#fbd38d">
                {busiest.orders} orders
              </text>
            </g>
          )}

          <line x1={PAD.l} x2={W - PAD.r} y1={PAD.t + PH} y2={PAD.t + PH}
            stroke="var(--border)" strokeWidth={1} />

          {[...actual, ...projected].map((p, i) => {
            if (i !== 0 && i !== actual.length - 1 && i !== totalPts - 1 && i % xLabelStep !== 0) return null;
            return (
              <text key={i} x={toX(i)} y={PAD.t + PH + 16} textAnchor="middle"
                fontSize={11} fill={i >= actual.length ? '#63b3ed' : 'var(--text-muted)'}>
                {p.date.slice(5)}
              </text>
            );
          })}

          {actual.map((p, i) => {
            const slotW = Math.max(PW / totalPts, 6);
            return (
              <g key={i}>
                <rect
                  x={toX(i) - slotW / 2} y={PAD.t}
                  width={slotW} height={PH}
                  fill="transparent"
                  style={{ cursor: 'crosshair' }}
                  onMouseEnter={() => setTooltip({ i, p })}
                  onMouseLeave={() => setTooltip(null)}
                />
                {tooltip?.i === i && (
                  <circle cx={toX(i)} cy={toY(p.cum)} r={5}
                    fill="#48bb78" stroke="var(--bg-card)" strokeWidth={2} />
                )}
              </g>
            );
          })}

          {tooltip && (() => {
            const { i, p } = tooltip;
            const tipW = 170, tipH = 48;
            const tipX = Math.min(Math.max(toX(i) - tipW / 2, PAD.l), W - PAD.r - tipW);
            const tipY = PAD.t + 4;
            return (
              <g style={{ pointerEvents: 'none' }}>
                <rect x={tipX} y={tipY} width={tipW} height={tipH}
                  rx={5} fill="var(--bg-secondary)" stroke="var(--border)" strokeWidth={1.5} />
                <text x={tipX + 10} y={tipY + 16} fontSize={11} fontWeight={700} fill="var(--text-secondary)">
                  {p.date}
                </text>
                <text x={tipX + 10} y={tipY + 31} fontSize={11} fill="#48bb78">
                  Cumulative: {fmtOrders(p.cum)} orders
                </text>
                <text x={tipX + 10} y={tipY + 44} fontSize={10} fill="var(--text-muted)">
                  This day: {p.orders} order{p.orders !== 1 ? 's' : ''}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 20, height: 3, background: '#48bb78', borderRadius: 2 }} />
          <span style={{ color: 'var(--text-secondary)' }}>Actual Orders</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#63b3ed" strokeDasharray="6,4" strokeWidth={2} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Projection (±1σ band)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#48bb78" strokeDasharray="3,4" strokeWidth={1} opacity={0.5} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Best case (+50%)</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={20} height={6}><line x1={0} y1={3} x2={20} y2={3} stroke="#fc8181" strokeDasharray="3,4" strokeWidth={1} opacity={0.5} /></svg>
          <span style={{ color: 'var(--text-secondary)' }}>Worst case (-50%)</span>
        </div>
      </div>

      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.5 }}>
        Projection uses a linear trend fitted to the last {lookback} days of order counts. Best/worst case = ±50% slope.
        Confidence band widens over time (square-root scaling).
      </p>
    </div>
  );
}
